import React, { useState } from 'react';
import galleryService from '../../services/galleryService';

function InvitationInput({
  galleryId,
  invitations = [],
  onInvitationAdded,
  onInvitationRemoved,
  onInvitationUpdated,
  disabled = false
}) {
  const [email, setEmail] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [updatingEmail, setUpdatingEmail] = useState(null);

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleAdd = async () => {
    setError('');

    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      setError('Please enter an email address');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    // Check for duplicates
    if (invitations.some(inv => inv.email.toLowerCase() === trimmedEmail)) {
      setError('This email has already been invited');
      return;
    }

    setLoading(true);
    try {
      const invitation = await galleryService.addInvitation(galleryId, trimmedEmail, canEdit);
      onInvitationAdded(invitation);
      setEmail('');
      setCanEdit(false);
    } catch (err) {
      setError('Failed to add invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (emailToRemove) => {
    try {
      await galleryService.removeInvitation(galleryId, emailToRemove);
      onInvitationRemoved(emailToRemove);
    } catch (err) {
      // Silently handle error - user already sees the result
    }
  };

  const handleCanEditChange = async (emailToUpdate, newCanEdit) => {
    setUpdatingEmail(emailToUpdate);
    try {
      await galleryService.updateInvitation(galleryId, emailToUpdate, newCanEdit);
      onInvitationUpdated(emailToUpdate, newCanEdit);
    } catch (err) {
      // Silently handle error - user already sees the result
    } finally {
      setUpdatingEmail(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="space-y-4">
      {/* Permission levels info */}
      <div className="text-xs text-gray-500 bg-primary-50 border border-primary-200 rounded-lg p-3">
        <p className="font-semibold mb-1">Permission levels:</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>View only:</strong> Can only view videos (default)</li>
          <li><strong>Can edit:</strong> Can add, remove, and reorder videos (cannot change gallery settings)</li>
        </ul>
      </div>

      {/* Add new invitation form */}
      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-start">
        <div className="flex-1 min-w-0">
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError('');
            }}
            onKeyPress={handleKeyPress}
            placeholder="user@example.com"
            disabled={disabled || loading}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          {error && (
            <p className="mt-1 text-xs text-red-600">{error}</p>
          )}
        </div>
        <div className="flex gap-2">
          <label className="flex items-center gap-1.5 px-2 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors" title="Allow editing videos">
            <input
              type="checkbox"
              checked={canEdit}
              onChange={(e) => setCanEdit(e.target.checked)}
              disabled={disabled || loading}
              className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-xs md:text-sm font-medium text-gray-700 whitespace-nowrap">Edit</span>
          </label>
          <button
            onClick={handleAdd}
            disabled={disabled || loading || !email.trim()}
            className="px-3 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors flex items-center gap-1.5 text-sm"
          >
            {loading ? (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>

      {/* Current invitations list */}
      {invitations.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">Current invitations:</p>
          <div className="space-y-2">
            {invitations.map((invitation) => (
              <div
                key={invitation.email}
                className="flex items-center gap-2 bg-gray-50 rounded-lg px-2 md:px-3 py-2"
              >
                <span className="flex-1 text-xs md:text-sm text-gray-700 truncate min-w-0">
                  {invitation.email}
                </span>
                <label className="flex items-center gap-1 cursor-pointer flex-shrink-0" title={invitation.canEdit ? 'Can edit videos' : 'View only'}>
                  <input
                    type="checkbox"
                    checked={invitation.canEdit}
                    onChange={(e) => handleCanEditChange(invitation.email, e.target.checked)}
                    disabled={updatingEmail === invitation.email}
                    className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-xs text-gray-600 whitespace-nowrap hidden sm:inline">
                    {updatingEmail === invitation.email ? 'Saving...' : 'Edit'}
                  </span>
                </label>
                <button
                  onClick={() => handleRemove(invitation.email)}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                  title="Remove invitation"
                >
                  <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {disabled && (
        <p className="text-sm text-gray-500 italic">
          Save the gallery first to add invitations
        </p>
      )}
    </div>
  );
}

export default InvitationInput;
